import React, { ChangeEvent, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Button, FoldableOpenMore, SquareButton } from '@app/core';
import { useTranslation } from 'react-i18next';
import { Input, NumberInput, Select, Toggle } from '@app/form';
import { ModeratedTaskSetting, useIsAdmin } from '../../queries';
import { format } from 'path';

const useStyles = createUseStyles((theme: Theme) => ({
  head: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    ...theme.fonts.h4,
    flex: 1,
  },
  tooltip: {
    marginLeft: theme.marginBase,
  },
  spacer: {
    border: {
      width: 0,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
    borderBottomWidth: 1,
    marginBottom: theme.marginBase * 3,
    marginTop: theme.marginBase * 3,
  },
  advancedButton: {
    marginBottom: theme.marginBase * 3,
  },
  inputSmallMargin: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const useOptions = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const formatMinutes = (minutes: number, emptyText?: string) => {
    if (minutes === 0 && emptyText) {
      return emptyText;
    }
    if (!('DurationFormat' in Intl)) {
      return `${minutes}min`;
    }

    // @ts-ignore
    return new Intl.DurationFormat(locale, { style: 'narrow' }).format({
      hours: Math.floor(minutes / 60),
      minutes: minutes % 60,
    });
  };

  const appointmentBufferOptions = [0, 10, 15, 20, 30, 45, 60, 90].map(
    (value) => ({
      value,
      label: formatMinutes(
        value,
        t('calendar.settings.noAppointmentBufferOption'),
      ),
    }),
  );

  const proposedMeetingIntervalOptions = [
    10, 15, 20, 30, 45, 60, 90, 120, 150,
  ].map((value) => ({
    value,
    label: formatMinutes(value),
  }));

  // prettier-ignore
  const bookingDelayOptions = [0, 3, 6, 12, 24, 36, 48, 24 * 3, 24 * 5, 24 * 7].map(
    (value) => {
      const label =
        value <= 48
          ? t('calendar.settings.bookingDelayOption', { count: value })
          : t('calendar.settings.bookingDelayOptionDays', {
              count: value / 24,
            });
      return { value, label };
    },
  );

  const maxDaysLimitOptions = [7, 14, 21, 30, 60, 90].map((value) => ({
    value,
    label: t('calendar.settings.maxDaysLimitOption', { count: value }),
  }));

  return {
    appointmentBufferOptions,
    proposedMeetingIntervalOptions,
    bookingDelayOptions,
    maxDaysLimitOptions,
  };
};

interface Props {
  onSettingChanged: (setting: ModeratedTaskSetting) => any;
  onRequestClose?: () => any;
  settings?: ModeratedTaskSetting;
}

export const CalendarSettingsPanel = ({
  onRequestClose,
  onSettingChanged,
  settings,
}: Props) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const isAdmin = useIsAdmin();

  const {
    appointmentBufferOptions,
    proposedMeetingIntervalOptions,
    bookingDelayOptions,
    maxDaysLimitOptions,
  } = useOptions();

  const onValueChange = useCallback(
    <TValue = any,>(value: TValue, name?: string) => {
      if (!settings || !onSettingChanged || !name) {
        return;
      }
      onSettingChanged({
        ...settings,
        [name]: value,
      });
    },
    [onSettingChanged, settings],
  );

  const onNumberInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.currentTarget;
      if (!name) {
        return;
      }
      onSettingChanged({
        ...settings,
        [name]: Number(value),
      } as ModeratedTaskSetting);
    },
    [onSettingChanged, settings],
  );

  return (
    <>
      <div className={classes.head}>
        <h3 className={classes.title}>{t('calendar.settings.title')}</h3>
        {onRequestClose && (
          <SquareButton
            onClick={onRequestClose}
            icon="ri-close-line"
            color="ink"
            overGray
          />
        )}
      </div>

      <hr className={classes.spacer} />

      <Toggle
        label={t('calendar.settings.showWeekends')}
        name="displayWeekendOnCalendar"
        checked={!!settings?.displayWeekendOnCalendar}
        onToggle={onValueChange}
      />

      {(settings?.groupMeeting || isAdmin) && (
        <>
          <hr className={classes.spacer} />
          <NumberInput
            name="numberOfParticipantByMeeting"
            label={t(`editSession.settings.numberOfParticipantByMeeting`, {
              context: settings?.groupMeeting ? 'group' : 'individual',
            })}
            min={1}
            noMargin
            value={settings?.numberOfParticipantByMeeting}
            onChange={onNumberInputChange}
          />
        </>
      )}

      <hr className={classes.spacer} />

      {!settings?.groupMeeting && (
        <Select
          label={t('calendar.settings.appointmentBuffer')}
          name="appointmentBuffer"
          value={settings?.appointmentBuffer || 0}
          onValueChange={onValueChange}
          options={appointmentBufferOptions}
          smallSelect
          noMargin
          containerClass={classes.inputSmallMargin}
        />
      )}

      <Select
        label={t('calendar.settings.bookingDelay')}
        name="bookingDelayInHours"
        value={settings?.bookingDelayInHours}
        onValueChange={onValueChange}
        options={bookingDelayOptions}
        smallSelect
        noMargin
        containerClass={classes.inputSmallMargin}
      />

      {settings?.groupMeeting && (
        <Select
          label={t('calendar.settings.maxDaysLimit')}
          name="maxDaysLimit"
          options={maxDaysLimitOptions}
          value={settings?.maxDaysLimit}
          onValueChange={onValueChange}
          smallSelect
          noMargin
        />
      )}

      {!settings?.groupMeeting && (
        <FoldableOpenMore
          label={t('calendar.settings.advanced')}
          className={classes.advancedButton}
        >
          <Select
            label={t('calendar.settings.proposedMeetingInterval')}
            name="proposedMeetingInterval"
            value={settings?.proposedMeetingInterval}
            options={proposedMeetingIntervalOptions}
            onValueChange={onValueChange}
            smallSelect
            noMargin
            containerClass={classes.inputSmallMargin}
          />

          <Select
            label={t('calendar.settings.maxDaysLimit')}
            name="maxDaysLimit"
            options={maxDaysLimitOptions}
            value={settings?.maxDaysLimit}
            onValueChange={onValueChange}
            smallSelect
            noMargin
          />
        </FoldableOpenMore>
      )}

      <div className={classes.buttonContainer}>
        <Button
          color="blue"
          variant="transparent"
          href={
            i18n.language === 'fr'
              ? 'https://tandemz.crisp.help/fr/article/comment-gerer-mes-disponibilites-pour-ma-session-y2aet/'
              : 'https://tandemz.crisp.help/en/article/how-do-i-manage-my-availabilities-for-my-session-1jthb4e/'
          }
          target="_blank"
        >
          {t('calendar.settings.help')}
        </Button>
      </div>
      {settings?.moderatorCalendarId && (
        <>
          <hr className={classes.spacer} />

          <Toggle
            label={t('calendar.settings.shouldCheckForConflicts')}
            name="shouldCheckForConflicts"
            checked={!!settings?.shouldCheckForConflicts}
            onToggle={onValueChange}
          />

          <Toggle
            label={t('calendar.settings.ignoreAvailableEvents')}
            name="ignoreAvailableEvents"
            checked={!!settings?.ignoreAvailableEvents}
            onToggle={onValueChange}
          />
        </>
      )}
    </>
  );
};
